import { cloneDeep } from 'lodash-es';
import {
    CountryNamesNotInAnyShippingZoneResponse,
    createShippingRateInZoneResponse,
    ShippingZoneResponse,
} from 'models/ppoints/ShippingZone';
import { PackageProviderName, SettingKey } from '../../../data/ppoints/constants';
import httpApi from '../../../hooks/api/httpApi';
import { PackageProvider } from '../../../models/ppoints/PackageProvider';
import { ProviderWithShippingZones } from '../../../models/ppoints/ProviderWithShippingZones';
import { ShopifyShippingZone } from '../../../models/ppoints/ShopifyShippingZone';
import { store } from '../../../redux/store';
import { objectHasKey } from '../../../utils/utils';
import PackageProviderService from './PackageProviderService';

const getShippingZones = async (ecomId: number | null) => {
    if (!ecomId) return [];
    const res = await httpApi.get<ShopifyShippingZone[]>(`/api/ecom/${ecomId}/shipping-zone`);
    return res.getData();
};

const getZoneById = async (ecomId: number, zoneId: number): Promise<ShopifyShippingZone | null> => {
    zoneId = +zoneId;
    const zones = await getShippingZones(ecomId);
    return zones.find((zone) => zone.id === zoneId) || null;
};

const getZoneIdsEnabledForProviderId = (providerId) => {
    const settings = store.getState().settings.settings;
    if (!settings) return [];
    const shippingZoneSetting = settings[SettingKey.zones_and_providers];
    let zones = Object.keys(shippingZoneSetting).filter((zoneId) =>
        (shippingZoneSetting[zoneId] as number[]).includes(providerId)
    );
    return zones;
};

const getShippingRatesForZoneId = async (ecomId: number, zoneId: number) => {
    const zone = await getZoneById(ecomId, zoneId);
    if (!zone) return null;
    return {
        price_based_shipping_rates: zone.price_based_shipping_rates,
        weight_based_shipping_rates: zone.weight_based_shipping_rates,
    };
};

/**
 * Returns all package providers and the shopify shipping zones they are enabled in
 *
 * @param ecomId
 * @param zones
 */
const getProvidersWithShippingZones = async (
    ecomId: number,
    zones: ShopifyShippingZone[]
): Promise<ProviderWithShippingZones[]> => {
    let methods: ProviderWithShippingZones[] = [];
    let allProviders = await PackageProviderService.getAllCached();

    for (let provider of allProviders) {
        let providerShippingData: ProviderWithShippingZones = {
            provider: provider,
            zones: [],
        };

        let zoneIds = getZoneIdsEnabledForProviderId(provider.id);
        for (let zoneId of zoneIds) {
            const zone = zones.find((zone) => zone.id === +zoneId);
            if (zone) {
                providerShippingData.zones.push(zone);
            }
        }
        methods.push(providerShippingData);
    }
    return methods;
};

const filterShippingZonesByAvailableProvider = async (
    providerName: PackageProviderName,
    zones: ShopifyShippingZone[] | undefined
) => {
    let allProviders = await PackageProviderService.getAllCached();
    let provider = allProviders.find((provider) => provider.name === providerName);

    let zonesFiltered: ShopifyShippingZone[] = [];
    let zoneIds = getZoneIdsEnabledForProviderId(provider?.id);
    for (let zoneId of zoneIds) {
        const zone = zones?.find((zone) => zone.id === +zoneId);
        if (zone) {
            zonesFiltered.push(zone);
        }
    }
    return zonesFiltered;
};

const updateShippingZonesAndProvidersState = (
    prevState,
    zoneId: number,
    provider: PackageProvider,
    isChecked: boolean
) => {
    let state = cloneDeep(prevState);

    if (!objectHasKey(prevState, zoneId)) {
        state[zoneId] = [];
    }

    if (isChecked) {
        let containsProvider = state[zoneId].includes(provider.id);
        if (!containsProvider) {
            state[zoneId].push(provider.id);
        }
    } else {
        state[zoneId] = state[zoneId].filter((providerId) => providerId !== provider.id);
    }

    state[zoneId].sort(function (a, b) {
        return a - b;
    });

    return state;
};

const getCountriesFromZone = (zone: ShopifyShippingZone) => {
    return zone.countries.map((country) => country.name);
};

const isProviderIdEnabledForZoneId = (setting: Record<number, number[]>, zoneId: number, providerId: number) => {
    let res;
    if (!objectHasKey(setting, zoneId)) {
        res = false;
    } else {
        res = setting[zoneId].includes(providerId);
    }
    // console.log(setting, zoneId, providerId, res);
    return res;
};

const createShippingRateInZone = async (
    ecomId: number | null,
    zone: ShopifyShippingZone,
    rateName: string,
    ratePrice: number,
    rateCurrency: string
) => {
    if (!ecomId) return Promise.reject();
    zone.profile_id = zone.profile_id.split('/').slice(-1)[0];
    zone.location_group_id = zone.location_group_id.split('/').slice(-1)[0];
    let res = await httpApi.post<createShippingRateInZoneResponse>(`/api/ecom/${ecomId}/create-shipping-rate`, {
        deliveryProfileId: zone.profile_id,
        locationGroupId: zone.location_group_id,
        zoneId: zone.id,
        rateName,
        ratePrice,
        rateCurrency,
    });
    return res;
};

const getCountryNamesNotInAnyShippingZone = async (ecomId: number): Promise<string[]> => {
    let res = await httpApi.get<CountryNamesNotInAnyShippingZoneResponse>(`/api/ecom/${ecomId}/shipping-zone/missing`);
    let data = res.getData();
    return data.missing_zones;
};

const createZone = async (ecomId: number, name = '', countries: string[]) => {
    let res = await httpApi.post<ShippingZoneResponse>(`/api/ecom/${ecomId}/shipping-zone/create`, {
        name,
        countries,
    });
    let data = res.getData();
    return data.missing_zones;
};

export default {
    getShippingZones,
    getZoneById,
    getZoneIdsEnabledForProviderId,
    getShippingRatesForZoneId,
    getProvidersWithShippingZones,
    updateShippingZonesAndProvidersState,
    getCountriesFromZone,
    isProviderIdEnabledForZoneId,
    createShippingRateInZone,
    filterShippingZonesByAvailableProvider,
    getCountryNamesNotInAnyShippingZone,
    createZone,
};
